<template>
    <div :class="wrapperClass" :id="dropdownID">
        <div :class="inputWrapperClass">
            <input :class="inputClass" type="text" readonly :value="currentDisplayValue" @focus="toggleCalendar(true)"
                @keydown.tab="toggleCalendar(false)" />
            <button :class="calendarButtonClass" @click="toggleCalendar()" tabindex="-1">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"
                    class="w-5 h-5 inline-block">
                    <path fill-rule="evenodd"
                        d="M5.75 2a.75.75 0 01.75.75V4h7V2.75a.75.75 0 011.5 0V4h.25A2.75 2.75 0 0118 6.75v8.5A2.75 2.75 0 0115.25 18H4.75A2.75 2.75 0 012 15.25v-8.5A2.75 2.75 0 014.75 4H5V2.75A.75.75 0 015.75 2zm-1 5.5c-.69 0-1.25.56-1.25 1.25v6.5c0 .69.56 1.25 1.25 1.25h10.5c.69 0 1.25-.56 1.25-1.25v-6.5c0-.69-.56-1.25-1.25-1.25H4.75z"
                        clip-rule="evenodd" />
                </svg>
            </button>
        </div>
        <div :class="calendarWrapperClass" v-if="currentPage && currentPage != 'none'">
            <div class="flex text-center items-center border-b">
                <button :class="moveClass(-1)" tabindex="-1" class="w-12 h-12" @click.prevent.stop="move(-1)">&lt;</button>
                <button tabindex="-1" :disabled="currentPage == 'years'"
                    class="h-12 hover:bg-gray-50 cursor-pointer flex-grow text-lg w-40" @click.prevent.stop="changePage">
                    <span v-if="currentPage == 'days'">{{ monthName(currentViewMonth) }} {{ currentViewYear }}</span>
                    <span v-else-if="currentPage == 'months'">{{ currentViewYear }}</span>
                    <span v-else-if="currentPage == 'years'">{{ currentViewYear - currentViewYear % 10 }}s</span>
                </button>
                <button :class="moveClass(1)" tabindex="-1" class="w-12 h-12" @click.prevent.stop="move(1)">&gt;</button>
            </div>
            <div v-if="currentPage == 'days'">
                <div class="grid grid-cols-7 text-center">
                    <div class="p-1 text-gray-500" v-for="d in [...Array(7).keys()]">
                        {{ dayShortName(d + 1) }}
                    </div>
                    <div v-for="d in [...Array((currentViewStartDay - 1 + 7) % 7).keys()]">
                        &nbsp;</div>
                    <div :class="dayClass(d + 1)" v-for="d in [...Array(currentViewEndDayOfMonth).keys()]"
                        @click="select(d + 1)">
                        {{ d + 1 }}
                    </div>
                </div>
            </div>
            <div v-if="currentPage == 'months'">
                <div class="grid grid-cols-3 text-center">
                    <div :class="monthClass(m)" v-for="m in [...Array(12).keys()]" @click.prevent.stop="selectMonth(m)">
                        {{ monthShortName(m) }}
                    </div>
                </div>
            </div>
            <div v-if="currentPage == 'years'">
                <div class="grid grid-cols-3 text-center">
                    <div :class="yearClass(y + (currentViewYear - currentViewYear % 10))" v-for="y in [...Array(10).keys()]"
                        @click.prevent.stop="selectYear(y + (currentViewYear - currentViewYear % 10))">
                        {{ y + (currentViewYear - currentViewYear % 10) }}
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<script lang="js">
import { add, endOfMonth, format, parse, startOfMonth } from 'date-fns';
import { computed, ref, nextTick } from 'vue';
export default {
    props: {
        // :lower-limit="new Date()"
        // :input-format="dateFormat"
        // :starting-view="departure_date ? 'day' : 'year'"
        modelValue: { type: String, default: null },
        minValue: null,
        maxValue: null,
        defaultView: { type: String, default: "days" },
        inputWrapperClass: { type: String, default: "relative" },
        inputClass: { type: String, default: "w-full" },
        selectedClass: { type: String, default: 'px-1 py-2 font-bold bg-gray-50' },
        outOfBoundsClass: { type: String, default: 'px-1 py-2 text-gray-300 pointer-events-none' },
        dayButtonClass: { type: String, default: "px-1 py-2 hover:bg-gray-50 cursor-pointer" },
        calendarButtonClass: { type: String, default: "absolute right-[1px] top-[1px] w-10 h-10 hover:bg-gray-50 cursor-pointer" },
        wrapperClass: { type: String, default: "" },
        calendarWrapperClass: { type: String, default: 'absolute z-10 bg-white shadow-xl' },
        parseValue: { type: Function, default: (v) => parse(v, 'yyyy-MM-dd', new Date()) },
        displayValue: { type: Function, default: (v) => format(parse(v, 'yyyy-MM-dd', new Date()), 'dd/MM/yyyy') },
    },
    emits: ['update:modelValue'],
    setup(props, { emit }) {

        var dt = new Date().getTime()
        var id = "xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
            var r = (dt + Math.random() * 16) % 16 | 0
            dt = Math.floor(dt / 16)
            return (c == "x" ? r : (r & 0x3) | 0x8).toString(16)
        })
        const dropdownID = ref('dd-' + id)
        const currentDisplayValue = computed(() => {
            return props.modelValue ? props.displayValue(props.modelValue) : ''
        })
        const monthName = (monthIndex) => ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'][monthIndex]
        const monthShortName = (monthIndex) => ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][monthIndex]
        const dayName = (dayIndex) => ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][dayIndex % 7]
        const dayShortName = (dayIndex) => ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][dayIndex % 7]
        const currentPage = ref(null)

        let removeClickListener = null
        function onClickOutside(id, callback) {
            const outsideClickListener = (event) => {
                const element = document.querySelector(`#${id}`)
                if (element && !element.contains(event.target)) {
                    removeClickListener()
                    setTimeout(callback, 0)
                }
            }

            const removeClickListener = () => {
                document.removeEventListener("click", outsideClickListener)
            }
            setTimeout(() => {
                document.addEventListener("click", outsideClickListener)
            }, 0)
            return removeClickListener
        }
        const shouldClose = ref(false)
        const toggleCalendar = (on = undefined) => {
            if (on === undefined) {
                if (!currentPage.value || currentPage.value == 'none') {
                    on = true
                } else {
                    on = false
                }
            }
            if (on) {
                currentPage.value = props.defaultView
                removeClickListener = onClickOutside(dropdownID.value, () => {
                    currentPage.value = 'none'
                })
            } else {
                if (removeClickListener) {
                    removeClickListener()
                }
                currentPage.value = 'none'
            }

        }
        function defaultViewDate() {
            if (props.modelValue) {
                const s = props.modelValue.trim().substring(0, Math.min(props.modelValue.length, 10))
                const d = parse(s, 'yyyy-MM-dd', 0)
                if (!isNaN(d.getTime())) {
                    return d
                }
            }
            return new Date()
        }
        const currentViewDate = ref(defaultViewDate())
        const select = (date) => {
            const v = format(new Date(currentViewDate.value).setDate(date), 'yyy-MM-dd')
            currentPage.value = 'none'
            emit('update:modelValue', v)
        }
        const selectMonth = (month) => {
            const d = new Date(currentViewDate.value)
            d.setMonth(month)
            currentViewDate.value = d
            currentPage.value = 'days'
        }
        const selectYear = (year) => {
            const d = new Date(currentViewDate.value)
            d.setFullYear(year)
            currentViewDate.value = d
            currentPage.value = 'months'
        }

        const changePage = () => {
            shouldClose.value = false
            if (currentPage.value == 'days') {
                currentPage.value = 'months'
            } else if (currentPage.value == 'months') {
                currentPage.value = 'years'
            }
        }
        const move = (offset) => {
            shouldClose.value = false
            let part = ''
            if (currentPage.value == 'days') {
                part = 'months'
                currentViewDate.value = startOfMonth(currentViewDate.value)
            } else if (currentPage.value == 'months') {
                part = 'years'
                currentViewDate.value = startOfMonth(currentViewDate.value)
            } else if (currentPage.value == 'years') {
                part = 'years'
                offset = offset * 10
                currentViewDate.value = startOfMonth(currentViewDate.value)
            }
            if (part) {
                const duration = {}
                duration[part] = offset
                currentViewDate.value = add(currentViewDate.value, duration)
            }
        }
        const currentDate = computed(() => {
            if (props.modelValue) {
                const s = props.modelValue.trim().substring(0, Math.min(props.modelValue.length, 10))
                const d = parse(s, 'yyyy-MM-dd', 0)
                if (!isNaN(d.getTime())) {
                    return d
                }
            }
            return null
        })
        const currentYear = computed(() => {
            if (currentDate.value) {
                return currentDate.value.getFullYear()
            }
            return null
        })
        const currentMonth = computed(() => {
            if (currentDate.value) {
                return currentDate.value.getMonth()
            }
            return null
        })
        const currentDayOfMonth = computed(() => {
            if (currentDate.value) {
                return currentDate.value.getDate()
            }
            return null
        })
        const currentViewYear = computed(() => {
            return currentViewDate.value.getFullYear()
        })
        const currentViewMonth = computed(() => {
            return currentViewDate.value.getMonth()
        })
        const currentViewDayOfMonth = computed(() => {
            return currentViewDate.value.getDate()
        })
        const currentViewDay = computed(() => {
            return currentViewDate.value.getDay()
        })
        const currentViewStart = computed(() => {
            return startOfMonth(currentViewDate.value)
        })
        const currentViewEnd = computed(() => {
            return endOfMonth(currentViewDate.value)
        })
        const currentViewStartDay = computed(() => {
            return startOfMonth(currentViewDate.value).getDay()
        })
        const currentViewEndDayOfMonth = computed(() => {
            return endOfMonth(currentViewDate.value).getDate()
        })
        const minDateValue = computed(() => {
            if (props.minValue) {
                if (props.minValue instanceof Date) {
                    return props.minValue
                }
                const s = props.minValue.trim().substring(0, Math.min(props.minValue.length, 10))
                const d = parse(s, 'yyyy-MM-dd', 0)
                if (!isNaN(d.getTime())) {
                    return d
                }
            }
            return null
        })
        const maxDateValue = computed(() => {
            if (props.maxValue) {
                if (props.maxValue instanceof Date) {
                    return props.maxValue
                }
                const s = props.maxValue.trim().substring(0, Math.min(props.maxValue.length, 10))
                const d = parse(s, 'yyyy-MM-dd', 0)
                if (!isNaN(d.getTime())) {
                    return d
                }
            }
            return null
        })
        const minYear = computed(() => {
            return minDateValue.value ? minDateValue.value.getFullYear() : 0
        })
        const minMonth = computed(() => {
            return minDateValue.value ? minDateValue.value.getMonth() : 0
        })
        const minDate = computed(() => {
            return minDateValue.value ? minDateValue.value.getDate() : 0
        })
        const maxYear = computed(() => {
            return maxDateValue.value ? maxDateValue.value.getFullYear() : 1000000
        })
        const maxMonth = computed(() => {
            return maxDateValue.value ? maxDateValue.value.getMonth() : 100
        })
        const maxDate = computed(() => {
            return maxDateValue.value ? maxDateValue.value.getDate() : 100
        })
        const dayClass = (d) => {
            if (currentViewYear.value === currentYear.value && currentViewMonth.value === currentMonth.value && currentDayOfMonth.value == d) {
                return props.selectedClass
            } else if (currentViewYear.value < minYear.value
                || (currentViewYear.value == minYear.value && currentViewMonth.value < minMonth.value)
                || (currentViewYear.value == minYear.value && currentViewMonth.value == minMonth.value && d < minDate.value)) {
                return props.outOfBoundsClass
            } else if (currentViewYear.value > maxYear.value
                || (currentViewYear.value == maxYear.value && currentViewMonth.value > maxMonth.value)
                || (currentViewYear.value == maxYear.value && currentViewMonth.value == maxMonth.value && d > maxDate.value)) {
                return props.outOfBoundsClass
            } else {
                return props.dayButtonClass
            }
        }
        const monthClass = (m) => {
            if (currentViewYear.value === currentYear.value && m === currentMonth.value) {
                return props.selectedClass
            } else if (currentViewYear.value < minYear.value
                || (currentViewYear.value == minYear.value && m < minMonth.value)) {
                return props.outOfBoundsClass
            } else if (currentViewYear.value > maxYear.value
                || (currentViewYear.value == maxYear.value && m > maxMonth.value)) {
                return props.outOfBoundsClass
            } else {
                return props.dayButtonClass
            }
        }
        const yearClass = (y) => {
            if (y === currentYear.value) {
                return props.selectedClass
            } else if (y < minYear.value) {
                return props.outOfBoundsClass
            } else if (y > maxYear.value) {
                return props.outOfBoundsClass
            } else {
                return props.dayButtonClass
            }
        }
        const moveClass = (o) => {
            if (currentPage.value == 'days') {
                if (o < 0 && currentViewYear.value == minYear.value && currentViewMonth.value <= minMonth.value) {
                    return "text-gray-300 pointer-events-none"
                } else if (o > 0 && currentViewYear.value == maxYear.value && currentViewMonth.value >= maxMonth.value) {
                    return "text-gray-300 pointer-events-none"
                }
            } else if (currentPage.value == 'months') {
                if (o < 0 && currentViewYear.value <= minYear.value) {
                    return "text-gray-300 pointer-events-none"
                } else if (o > 0 && currentViewYear.value >= maxYear.value) {
                    return "text-gray-300 pointer-events-none"
                }
            } else if (currentPage.value == 'years') {
                o = 10 * o
                if (o < 0 && (currentViewYear.value + o) < (minYear.value - minYear.value % 10)) {
                    return "text-gray-300 pointer-events-none"
                } else if (o > 0 && (currentViewYear.value + o) > (maxYear.value + 10 - maxYear.value % 10)) {
                    return "text-gray-300 pointer-events-none"
                }
            }
            return "hover:bg-gray-50 cursor-pointer"
        }
        return {
            dropdownID,
            currentDisplayValue,
            emit,
            monthName,
            monthShortName,
            dayName,
            dayShortName,
            toggleCalendar,
            select,
            selectMonth,
            selectYear,
            changePage,
            move,
            currentPage,
            shouldClose,
            currentDate, currentYear, currentMonth, currentDayOfMonth,
            currentViewDate, currentViewYear, currentViewMonth, currentViewDayOfMonth, currentViewDay,
            currentViewStart, currentViewStartDay, currentViewEnd, currentViewEndDayOfMonth,
            dayClass, monthClass, yearClass, moveClass
        }
    }
}
</script>